
import { defineComponent, onBeforeMount, reactive } from 'vue'

// UI
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// Module Components
import FlightDeparturesActions from '@/modules/FlightDeparturesModule/components/FlightDeparturesActions/index.vue'
import FlightDeparturesFilters from '@/modules/FlightDeparturesModule/components/FlightDeparturesFilters/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { FlightDeparture, GetFlightDeparturesParams } from '@/new-services/types/flight-departures'

// Composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'
import { useFile } from '@/common/composable/useFile'

// constants
import { FlightDeparturesColumns } from '@/core/constants/columns/FlightDepartures.columns'

// @new-services
import flightDeparturesAPI from '@/new-services/flightDepartures.service'

export default defineComponent({
    components: {
        PageTitle,
        FailedFetch,
        TheButton,
        //
        FlightDeparturesActions,
        NewTable,
        NewPagination,
        NewLimit,
        Cheap,
        Loader,
        FlightDeparturesFilters,
    },
    setup() {
        const { saveFile } = useFile()

        const state = reactive({
            limit: 50,
            flight_departures: new DataState<FlightDeparture>({}),
            is_excel_loading: false,
        })

        const filters_state = reactive({
            departureAtFrom: null as string | null,
            departureAtTo: null as string | null,
            arrivalAtFrom: null as string | null,
            arrivalAtTo: null as string | null,
            cityIdFrom: null as number | null,
            cityIdTo: null as number | null,
            status: null as number | null,
            number: null as string | null,
        })

        const loadFlightDepartures = async (page: number) => {
            state.flight_departures.pagination.current_page = page

            try {
                state.flight_departures.is_loading = true

                const params = {
                    page,
                    limit: state.limit,
                    ...filters_state,
                }

                const data = await flightDeparturesAPI.getList(params)
                state.flight_departures.list = data.data.data

                state.flight_departures.pagination.total_pages = data.data.meta.last_page
                state.flight_departures.pagination.total_items = data.data.meta.total

                state.flight_departures.is_failed = false
                state.flight_departures.failure.error_state.is_cancel = false
            } catch (error) {
                state.flight_departures.failure.catchError(error)

                if (state.flight_departures.failure.error_state.is_cancel) {
                    state.flight_departures.is_failed = true
                }
            } finally {
                if (!state.flight_departures.failure.error_state.is_cancel) {
                    state.flight_departures.is_loading = false
                }
            }
        }

        const { actions_state, startCreate, startUpdate } = useActions<FlightDeparture>()
        const notifications = useNotifications()
        const { getFormData } = useFormData()

        const onFlightDepartureCreate = async (files: File[]) => {
            try {
                actions_state.is_actions_loading = true

                const payload = { files }

                await flightDeparturesAPI.createFlightDeparture(getFormData(payload))
                notifications.notify({ title: 'График создан', type: 'success' })

                await loadFlightDepartures(state.flight_departures.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при создании графика' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onFlightDepartureUpdate = async (status: number) => {
            if (!actions_state.initial_value) return

            try {
                actions_state.is_actions_loading = true

                const { id, number, fromCityId, toCityId, departureAt, arrivalAt } =
                    actions_state.initial_value
                const payload = {
                    number,
                    fromCityId,
                    toCityId,
                    departureAt,
                    status,
                    arrivalAt,
                }

                await flightDeparturesAPI.updateFlightDeparture(id, payload)
                notifications.notify({ title: 'График сохранен', type: 'success' })

                await loadFlightDepartures(state.flight_departures.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при редактировании графика' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadFlightDepartures(state.flight_departures.pagination.current_page)
        }

        const onChangePage = (page: number) => {
            state.flight_departures.pagination.current_page = page
            loadFlightDepartures(page)
        }

        const getCheapClass = (status: string) => {
            let class_name = 'small max-w-min '
            class_name += status === 'Доступен' ? 'green' : 'red'

            return class_name
        }

        const exportExcel = async () => {
            try {
                state.is_excel_loading = true

                const response = await flightDeparturesAPI.exportExcel(filters_state)

                saveFile(response.data, 'График вылетов')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                state.is_excel_loading = false
            }
        }

        const setFilters = (params: GetFlightDeparturesParams) => {
            filters_state.departureAtFrom = params.departureAtFrom
            filters_state.departureAtTo = params.departureAtTo
            filters_state.arrivalAtFrom = params.arrivalAtFrom
            filters_state.arrivalAtTo = params.arrivalAtTo
            filters_state.cityIdFrom = params.cityIdFrom
            filters_state.cityIdTo = params.cityIdTo
            filters_state.status = params.status
            filters_state.number = params.number
        }

        const unsetFilters = () => {
            filters_state.departureAtFrom = null
            filters_state.departureAtTo = null
            filters_state.arrivalAtFrom = null
            filters_state.arrivalAtTo = null
            filters_state.cityIdFrom = null
            filters_state.cityIdTo = null
            filters_state.status = null
            filters_state.number = null
        }

        const applyFilters = (params: GetFlightDeparturesParams) => {
            setFilters(params)
            loadFlightDepartures(1)
        }

        const resetFilters = () => {
            unsetFilters()
            loadFlightDepartures(1)
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            const dateSecond = dateStr.split(' ')[1]
            return dateFirst.split('-').reverse().join('.') + ' ' + dateSecond
        }

        onBeforeMount(() => {
            loadFlightDepartures(1)
        })

        return {
            state,
            actions_state,
            //
            exportExcel,
            applyFilters,
            resetFilters,
            loadFlightDepartures,
            onFlightDepartureCreate,
            onFlightDepartureUpdate,
            startCreate,
            startUpdate,
            onLimitChange,
            onChangePage,
            getCheapClass,
            formatDate,
            //
            FlightDeparturesColumns,
        }
    },
})
